import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  progress: number;
  lastModified: number;
  url?: string;
}

export interface PendingFileOpen {
  fileName: string;
  contextKey: string;
  attachmentId?: string;
  commentId?: string;
  versionId?: number;
}

export interface FileUploadStoreState {
  files: FileMetadata[];
  uploadContexts: Record<string, string>;
  pendingOpen: PendingFileOpen | null;
  activeContextKey: string | null;
  openContextKeys: Set<string>;
  appendFile: (file: File, customId?: string) => string;
  setProgress: (id: string, progress: number) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  setUploadContext: (fileName: string, contextKey: string) => void;
  requestFileOpen: (fileName: string) => void;
  requestContextOpen: (
    contextKey: string,
    opts?: { attachmentId?: string; commentId?: string; versionId?: number },
  ) => void;
  clearPendingOpen: () => void;
  registerOpenContext: (contextKey: string) => void;
  unregisterOpenContext: (contextKey: string) => void;
}

const useUploadedFilesStore = create<FileUploadStoreState>()(
  devtools((set, get) => ({
    files: [],
    uploadContexts: {},
    pendingOpen: null,
    activeContextKey: null,
    openContextKeys: new Set<string>(),

    appendFile: (file: File, customId?: string) => {
      const id = customId || `${file.name}-${Date.now()}-${Math.random()}`;

      const metadata: FileMetadata = {
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0,
        lastModified: file.lastModified,
        url: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      };

      set((state) => ({
        files: [...state.files, metadata],
      }));

      return id;
    },

    setProgress: (id: string, progress: number) =>
      set((state) => ({
        files: state.files.map((f) => (f.id === id ? { ...f, progress } : f)),
      })),

    removeFile: (id: string) =>
      set((state) => ({
        files: state.files.filter((f) => f.id !== id),
      })),

    clearFiles: () =>
      set({
        files: [],
        uploadContexts: {},
        pendingOpen: null,
        activeContextKey: null,
        openContextKeys: new Set(),
      }),

    setUploadContext: (fileName: string, contextKey: string) =>
      set((state) => ({
        uploadContexts: { ...state.uploadContexts, [fileName]: contextKey },
      })),

    requestFileOpen: (fileName: string) => {
      const contextKey = get().uploadContexts[fileName];
      if (contextKey) {
        if (get().openContextKeys.has(contextKey)) return;
        set({ pendingOpen: { fileName, contextKey }, activeContextKey: contextKey });
      } else {
        const file = get().files.find((f) => f.name === fileName);
        if (file?.url) window.open(file.url, '_blank');
      }
    },

    requestContextOpen: (contextKey: string, opts) =>
      set({
        pendingOpen: {
          fileName: '',
          contextKey,
          attachmentId: opts?.attachmentId,
          commentId: opts?.commentId,
          versionId: opts?.versionId,
        },
      }),

    clearPendingOpen: () => set({ pendingOpen: null }),

    registerOpenContext: (contextKey: string) =>
      set((state) => {
        const next = new Set(state.openContextKeys);
        next.add(contextKey);
        return { openContextKeys: next };
      }),

    unregisterOpenContext: (contextKey: string) =>
      set((state) => {
        const next = new Set(state.openContextKeys);
        next.delete(contextKey);
        return { openContextKeys: next };
      }),
  })),
);

export default useUploadedFilesStore;
